/**
 * @file        src/components/LoadBar.jsx
 * @description Global top progress bar. Appears whenever any backend request is
 *              in flight (fed by the axios in-flight counter in api/client.js),
 *              so slow APIs never look like a frozen page — mutations, drawers
 *              and background refetches included. NProgress-style: waits 200ms
 *              before showing (fast responses never flash), trickles toward
 *              ~90%, then sweeps to 100% and fades when the last request lands.
 * @author      Kinetika Labs
 * @created     2026-08-04
 * @changelog
 *   - 2026-08-04 Claude — Initial.
 */
import { useEffect, useRef, useState } from 'react';
import { onNetworkActivity } from '../api/client';

const SHOW_DELAY_MS = 200;   // grace period before the bar appears
const TRICKLE_MS = 280;      // trickle step interval
const DONE_SWEEP_MS = 260;   // time for the width → 100% sweep
const FADE_MS = 300;         // opacity fade after the sweep

export default function LoadBar() {
  const [progress, setProgress] = useState(0); // 0 = hidden
  const [fading, setFading] = useState(false);
  // Timers live in a ref: the activity callback fires outside React's lifecycle.
  const t = useRef({ show: null, trickle: null, done: null, reset: null });

  useEffect(() => {
    const timers = t.current;
    const stop = (k) => { clearTimeout(timers[k]); clearInterval(timers[k]); timers[k] = null; };

    const unsub = onNetworkActivity((active) => {
      if (active > 0) {
        // New activity while finishing? Cancel the wind-down and keep going.
        stop('done'); stop('reset');
        setFading(false);
        if (!timers.show && !timers.trickle) {
          timers.show = setTimeout(() => {
            timers.show = null;
            setProgress(14);
            timers.trickle = setInterval(
              () => setProgress((p) => Math.min(90, p + (92 - p) * 0.06)),
              TRICKLE_MS,
            );
          }, SHOW_DELAY_MS);
        }
      } else {
        stop('show'); stop('trickle');
        setProgress((p) => {
          if (p === 0) return 0; // finished inside the grace period — never shown
          timers.done = setTimeout(() => {
            setFading(true);
            timers.reset = setTimeout(() => { setProgress(0); setFading(false); }, FADE_MS);
          }, DONE_SWEEP_MS);
          return 100;
        });
      }
    });

    return () => { unsub(); ['show', 'trickle', 'done', 'reset'].forEach(stop); };
  }, []);

  if (progress === 0) return null;
  return (
    <div className={`load-bar ${fading ? 'fade' : ''}`} role="progressbar" aria-label="Loading">
      <div className="load-bar-fill" style={{ width: `${progress}%` }} />
    </div>
  );
}
